type Result<T> = { value: T } | { error: unknown };
type Batch<T> = {
  keys: string[]; next: number; pending: number;
  results: Map<string, Result<T>>;
  request: ((key: string, signal: AbortSignal) => Promise<T>) | null;
  resolve: ((result: Map<string, Result<T>> | null) => void) | null;
  timer: ReturnType<typeof setTimeout> | null;
};

// One instance for the document lifetime, including logout/account replacement.
// Logical cancellation never releases a physical slot. An ignoring transport must
// settle before latest work can dispatch; deadline releases callers, not slots.
export class ReplyWork<T> {
  private batch: Batch<T> | null = null;
  private physical = new Set<AbortController>();
  snapshot(): { physical: number; queued: number } {
    return { physical: this.physical.size, queued: this.batch ? this.batch.keys.length - this.batch.next : 0 };
  }
  cancel(): void {
    const batch = this.batch;
    if (batch) this.finish(batch, null);
    for (const controller of this.physical) controller.abort();
  }
  run(keys: string[], request: (key: string, signal: AbortSignal) => Promise<T>): Promise<Map<string, Result<T>> | null> {
    this.cancel();
    if (keys.length > 100) throw new Error('Reply source page exceeds 100 rows');
    return new Promise(resolve => {
      const batch: Batch<T> = { keys: [...new Set(keys)], next: 0, pending: 0, results: new Map(), request, resolve, timer: null };
      this.batch = batch;
      batch.timer = setTimeout(() => {
        if (this.batch !== batch) return;
        const results = new Map(batch.results);
        for (const key of batch.keys) if (!results.has(key)) results.set(key, { error: new Error('Reply source timeout') });
        this.finish(batch, results);
        for (const controller of this.physical) controller.abort();
      }, 15_000);
      this.pump();
    });
  }
  private finish(batch: Batch<T>, result: Map<string, Result<T>> | null): void {
    if (this.batch !== batch) return;
    this.batch = null;
    if (batch.timer !== null) clearTimeout(batch.timer);
    const resolve = batch.resolve;
    // Settled physical reactions may retain this shell, never the old queue/cache
    // or request/account callback. Public awaiters also settle on supersession.
    batch.keys = []; batch.results = new Map(); batch.request = null; batch.resolve = null; batch.timer = null;
    resolve?.(result);
  }
  private pump(): void {
    const batch = this.batch;
    if (!batch) return;
    while (this.physical.size < 4 && batch.next < batch.keys.length) {
      const key = batch.keys[batch.next++]!;
      const controller = new AbortController();
      this.physical.add(controller); batch.pending++;
      // The adapter promise includes response-body consumption. Do not race it
      // with abort/deadline or release this slot in cancel().
      let transport: Promise<T>;
      try { transport = batch.request!(key, controller.signal); }
      catch (error) { transport = Promise.reject(error); }
      void transport.then(value => {
        if (this.batch === batch) batch.results.set(key, { value });
      }, error => {
        if (this.batch === batch) batch.results.set(key, { error });
      }).finally(() => {
        this.physical.delete(controller); batch.pending--;
        this.pump();
      });
    }
    if (batch.next === batch.keys.length && batch.pending === 0) this.finish(batch, batch.results);
  }
}
